<template>
  <div>
    <div class="row q-col-gutter-md">
      <model-form-input
        field="config.donationTitle"
        value-type="String"
        :class="currentSite.sendToDonorPerfect ? 'col-4' : 'col-6'"
        label="Donation Title"
      >
        <q-tooltip>
          The title shown on the donation widget.
        </q-tooltip>
      </model-form-input>
      <model-form-select
        field="config.donationDefaultAmounts"
        value-type="Array"
        class="col-4"
        label="Default Donation Amount"
        multiple
        :options="options"
        use-chips
        stack-label
        clearable
        max-values="5"
        :hint="`${$t('form.hint.maxSelections', { count: 5 })}; ${$t('form.hint.mobileDisplayLimit', { count: 3 })}`"
        :rules="[val => val !== null || options.length === 0 || 'Field is required']"
      >
        <template #selected-item="scope">
          <q-chip
            v-if="scope.opt"
            removable
            dense
            :tabindex="scope.tabindex"
            @remove="scope.removeAtIndex(scope.index)"
          >
            <i18n-n
              :value="scope.opt"
              format="currency"
              scope="global"
            />
          </q-chip>
        </template>
        <template #option="scope">
          <q-item
            v-bind="scope.itemProps"
            v-on="scope.itemEvents"
          >
            <q-item-section>
              <i18n-n
                :value="scope.opt"
                format="currency"
                scope="global"
              />
            </q-item-section>
          </q-item>
        </template>
      </model-form-select>

      <model-form-checkbox
        field="config.allowDonationHonoree"
        value-type="Boolean"
        class="col-6"
        label="Allow Donation Honoree"
      >
        <q-tooltip>
          Allow guests to enter a honoree name when donating.
        </q-tooltip>
      </model-form-checkbox>

      <model-form-input
        field="config.donationButton"
        value-type="String"
        class="col-2"
        label="Donation Button"
      >
        <q-tooltip>
          The button text shown on the donation widget.
        </q-tooltip>
      </model-form-input>
      <button-style-editor
        class="row q-col-gutter-md q-mb-md items-start"
        :class="currentSite.sendToDonorPerfect ? 'col-9' : 'col-12'"
        :button-type="section.config.buttonType"
        :color-name="section.config.buttonColor"
        :custom-color-hex="section.config.buttonColorHex"
        :button-border-radius="section.config.buttonBorderRadius"
        @update:model-value="setButtonStyle"
      />
      <model-form-input
        v-if="currentSite.sendToDonorPerfect"
        field="config.donorPerfectCampaignCode"
        value-type="String"
        class="col-3"
        label="Donor Perfect Campaign Code"
      >
        <q-tooltip>
          The corresponding DonorPerfect campaign code (Leave blank to not track on DonorPerfect or send automatic thank you email).
        </q-tooltip>
      </model-form-input>
      <model-form-input
        v-if="currentCircuit.allowTaxDeductibleDonations"
        field="config.donationLegalName"
        value-type="String"
        class="col-6"
        label="Donation Legal Name"
      >
        <q-tooltip>
          The 501(c)(3) legal name of the donation. Used for creating a receipt for the donation.
        </q-tooltip>
      </model-form-input>
      <model-form-checkbox
        v-if="currentCircuit.allowTaxDeductibleDonations"
        field="config.sendTaxDeductibleReceipt"
        value-type="Boolean"
        class="col-6"
        label="Send Tax Deductible Receipt"
        :disabled="!section.config.donationLegalName"
      >
        <q-tooltip>
          Whether to send a tax deductible receipt for the donation. Requires a legal name to be set.
        </q-tooltip>
      </model-form-checkbox>
      <color-selection
        class="col-6"
        :color-name="section.config.blockBackgroundColor"
        :custom-color-hex="section.config.blockBackgroundColorHex"
        label="Widget Background Color"
        @update:model-value="setColor"
      />
      <model-form-select
        field="config.blockPosition"
        value-type="String"
        class="col-6"
        label="Widget Position"
        :options="$mh.asOptions(TEXT_POSITION_TYPES)"
        map-options
        emit-value
      />
    </div>
    <div class="row q-col-gutter-md q-mb-xl">
      <model-form-input
        field="config.donationDescription"
        value-type="String"
        class="col"
        label="Donation Description"
        type="textarea"
        maxlength="255"
        hint="Maximum 255 characters allowed"
      >
        <q-tooltip>
          The info description shown on the donation widget.
        </q-tooltip>
      </model-form-input>
    </div>
    <preview
      :section="section"
      class="q-mb-xl"
    />
  </div>
</template>

<script>
import CheckoutData from 'src/models/order/CheckoutData'
import Order from 'src/models/order/Order'
import Section, { TEXT_POSITION_TYPES } from 'src/models/page/Section'
import ButtonStyleEditor from 'src/modules/admin/components/ButtonStyleEditor'
import ColorSelection from 'src/modules/admin/components/ColorSelection'
import Preview from 'src/modules/consumer/components/Preview'

export default {
  name: 'DonationWidgetEditor',
  components: {
    Preview,
    ButtonStyleEditor,
    ColorSelection
  },
  configuration: ['currentSite', 'currentCircuit'],
  props: {
    section: {
      type: Section,
      required: true
    }
  },
  data () {
    return {
      checkoutData: new CheckoutData(),
      order: new Order({}, {
        orderItems: {
          itemOrderItem: {
            item: true,
            itemVariation: true,
            itemModifiers: {
              modifier: true
            }
          },
          ticket: {
            fields: 'all',
            showing: {
              movie: 'default'
            }
          },
          voucherPurchase: {
            voucherType: true,
            card: 'default'
          },
          membershipInterval: 'default',
          tax: true,
          discount: 'default',
          voucher: {
            voucherType: true
          },
          voucherType: true,

          order: 'default'
        },
        payments: {
          giftCard: 'default'
        },
        appliedRewards: true,
        user: {
          userClass: true,
          seatPreferences: true,
          membership: {
            members: {
              userClass: true,
              membershipType: true,
              vouchers: {
                voucherType: true
              }
            },
            owner: {
              fields: ['id']
            }
          },
          membershipType: true,
          vouchers: {
            voucherType: true
          },
          credit: 'default'
        },
        independentVouchers: {
          voucherType: true
        },
        giftCards: 'default',
        registerStation: true,
        stripePaymentIntentClientSecret: true
      })
    }
  },
  computed: {
    options () {
      const options = []
      for (let i = 1; i <= 100; i++) {
        options.push(i)
      }
      return options
    }
  },
  created () {
    _.extend(this, { TEXT_POSITION_TYPES })
  },
  unmounted () {
    this.order?.destroy()
  },
  methods: {
    setButtonStyle (buttonType, colorName, customColorHex, buttonRadius) {
      this.section.config.buttonType = buttonType
      this.section.config.buttonColor = colorName
      this.section.config.buttonColorHex = customColorHex
      this.section.config.buttonBorderRadius = buttonRadius
    },
    setColor (colorName, customColorHex) {
      this.section.config.blockBackgroundColor = colorName
      this.section.config.blockBackgroundColorHex = customColorHex
    }
  }

}

</script>
